import React, { FC, useMemo, useState } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import {
  useMinAgeAllowed,
  useClientIdSetting,
  useBackofficeParticipantPhoneNumberSetting,
  useBackofficeParticipantDateOfBirthSetting,
} from "@/hooks/useSetting";
import Select from "@/components/Select";
import Typography from "@mui/material/Typography";
import { getUSStatesForSelect } from "@/utils/us-states.enum";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import PhoneNumberInput from "@/components/PhoneNumberInput";
import Datepicker from "@/components/Datepicker";
import dayjs, { Dayjs } from "dayjs";
import registerParticipantValidationSchema, {
  ParticipantRegisterFormData,
} from "./validation.schema";
import {
  useUpdateParticipant,
  useSoftDeleteParticipant,
} from "@/hooks/useParticipants";
import {
  MobileUserStatusEnum,
  Participant,
  ParticipantStatus,
  ParticipantUpdatePayload,
} from "@/domain/participant.interface";
import AlertDialog from "@/components/AlertDialog";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";
import { useNavigate } from "react-router-dom";

type ParticipantUpdateModalProps = {
  open: boolean;
  participant: Participant;
  onClose: () => void;
  country: string;
};

const ParticipantUpdateModal: FC<ParticipantUpdateModalProps> = ({
  open,
  participant,
  onClose,
  country,
}) => {
  const { t } = useTranslation("participant");
  const { t: tCommon } = useTranslation("common");
  const { userInfo, userHasAccessToCode } = useUserStore();
  const { showSnackBar, showSimpleAlert } = useFeedBack();
  const theme = useAppTheme();
  const navigate = useNavigate();

  const createAuditLog = useCreateAuditlog();
  const { isPending, mutateAsync } = useUpdateParticipant();
  const softDeleteParticipant = useSoftDeleteParticipant();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToggleMobileLoginModal, setShowToggleMobileLoginModal] =
    useState(false);
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);

  const isMobileLoginActive =
    participant.mobileUser.status === MobileUserStatusEnum.ACTIVE;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleToggleMobileLogin = () => {
    handleMenuClose();
    const permissionCode = isMobileLoginActive
      ? "PARTICIPANT_DISABLE_MOBILE_LOGIN"
      : "PARTICIPANT_ENABLE_MOBILE_LOGIN";

    if (userHasAccessToCode(permissionCode)) {
      setShowToggleMobileLoginModal(true);
    } else {
      setShowPermissionDeniedModal(true);
    }
  };

  const handleConfirmToggleMobileLogin = async () => {
    const newStatus = isMobileLoginActive
      ? MobileUserStatusEnum.INACTIVE
      : MobileUserStatusEnum.ACTIVE;

    try {
      await mutateAsync(
        {
          id: participant._id,
          payload: {
            firstName: participant.firstName,
            lastName: participant.lastName,
            email: participant.email,
            countryPhoneCode: participant.countryPhoneCode,
            phoneNumber: participant.phoneNumber,
            updatedBy: userInfo?._id as string,
            cin: participant.cin,
            mobileStatus: newStatus,
            address: participant.address,
          },
        },
        {
          onSuccess: () => {
            showSnackBar(
              "The participant mobile access has been updated.",
              "success",
            );
            onClose();
          },
          onError: (error: ErrorResponse) => {
            const message = Array.isArray(error.message)
              ? error.message[0]
              : error.message;
            showSnackBar(
              message || "Failed to update participant mobile access",
              "error",
            );
          },
        },
      );
    } catch {
      showSnackBar("Failed to update participant mobile access", "error");
    }
    setShowToggleMobileLoginModal(false);
  };

  const handleDelete = () => {
    handleMenuClose();
    if (userHasAccessToCode("PARTICIPANT_DELETE_CLIENT")) {
      setShowDeleteModal(true);
    } else {
      setShowPermissionDeniedModal(true);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await softDeleteParticipant.mutateAsync(participant._id, {
        onSuccess: () => {
          showSnackBar("The registry has been deleted", "success");
          onClose();
          navigate("/participants");
        },
        onError: (error: ErrorResponse) => {
          const message = Array.isArray(error.message)
            ? error.message[0]
            : error.message;
          showSnackBar(message || "Failed to delete participant", "error");
        },
      });
    } catch {
      showSnackBar("Failed to delete participant", "error");
    }
    setShowDeleteModal(false);
  };

  const minAgeAllowed = useMinAgeAllowed();
  const clientIdSetting = useClientIdSetting();
  const backofficeParticipantPhoneNumberSetting =
    useBackofficeParticipantPhoneNumberSetting();
  const backofficeParticipantDateOfBirthSetting =
    useBackofficeParticipantDateOfBirthSetting();

  // Use backofficeParticipantPhoneNumber setting (respect DB value only)
  const phoneMandatory = useMemo(
    () => backofficeParticipantPhoneNumberSetting === "mandatory",
    [backofficeParticipantPhoneNumberSetting],
  );
  // Use backofficeParticipantDateOfBirth setting
  const dateOfBirthMandatory = useMemo(
    () => backofficeParticipantDateOfBirthSetting === "mandatory",
    [backofficeParticipantDateOfBirthSetting],
  );
  const dateOfBirthVisible = useMemo(
    () => backofficeParticipantDateOfBirthSetting !== "hidden",
    [backofficeParticipantDateOfBirthSetting],
  );
  const cinMandatory = useMemo(
    () => clientIdSetting === "mandatory",
    [clientIdSetting],
  );

  const schema = useMemo(() => {
    return registerParticipantValidationSchema(
      tCommon,
      country,
      dateOfBirthMandatory,
      phoneMandatory as boolean,
      minAgeAllowed,
      cinMandatory,
    );
  }, [
    tCommon,
    country,
    dateOfBirthMandatory,
    phoneMandatory,
    minAgeAllowed,
    cinMandatory,
  ]);

  const defaultValues = {
    firstName: participant.firstName,
    lastName: participant.lastName,
    email: participant.email,
    phoneNumber: participant.phoneNumber,
    dateOfBirth: participant.dateOfBirth,
    cin: participant.cin,
    house: participant.address?.house || "",
    locality: participant.address?.locality || "",
    postalCode: participant.address?.postalCode || "",
    primaryAddressLine1:
      participant.address?.addressLine1 || participant.address?.house || "",
    primaryAddressLine2: participant.address?.addressLine2 || "",
    primaryCity:
      participant.address?.city || participant.address?.locality || "",
    primaryState: participant.address?.state || "",
    primaryZipCode:
      participant.address?.zipCode || participant.address?.postalCode || "",
    primaryCounty: participant.address?.county || "",
    secondaryAddressLine1: participant.address?.secondaryAddressLine1 || "",
    secondaryAddressLine2: participant.address?.secondaryAddressLine2 || "",
    secondaryCity: participant.address?.secondaryCity || "",
    secondaryState: participant.address?.secondaryState || "",
    secondaryZipCode: participant.address?.secondaryZipCode || "",
    secondaryCounty: participant.address?.secondaryCounty || "",
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<ParticipantRegisterFormData>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues,
  });

  const onSubmit = async (data: ParticipantRegisterFormData) => {
    const payload = {
      firstName: data.firstName as string,
      lastName: data.lastName as string,
      email: data.email as string,
      countryPhoneCode: country === "US" ? "1" : "44",
      phoneNumber: data.phoneNumber as string,
      updatedBy: userInfo?._id as string,
      dateOfBirth: data.dateOfBirth
        ? dayjs(data.dateOfBirth as string).toDate()
        : null,
      cin: (data.cin || "") as string,
      address: {
        house: (data as any).house || (data as any).primaryAddressLine1 || "",
        locality: (data as any).locality || (data as any).primaryCity || "",
        postalCode:
          (data as any).postalCode || (data as any).primaryZipCode || "",
        addressLine1: (data as any).primaryAddressLine1 || "",
        addressLine2: (data as any).primaryAddressLine2 || "",
        city: (data as any).primaryCity || "",
        state: (data as any).primaryState || "",
        zipCode: (data as any).primaryZipCode || "",
        county: (data as any).primaryCounty || "",
        secondaryAddressLine1: (data as any).secondaryAddressLine1 || "",
        secondaryAddressLine2: (data as any).secondaryAddressLine2 || "",
        secondaryCity: (data as any).secondaryCity || "",
        secondaryState: (data as any).secondaryState || "",
        secondaryZipCode: (data as any).secondaryZipCode || "",
        secondaryCounty: (data as any).secondaryCounty || "",
      },
    } as ParticipantUpdatePayload;

    try {
      await mutateAsync(
        {
          id: participant._id,
          payload,
        },
        {
          onSuccess: (data) => {
            onClose();
            showSnackBar("The client has been saved", "success");

            createAuditLog.mutate({
              appType: "WEB_BACK_OFFICE",
              module: MODULE_NAME.PARTICIPANTS,
              option: OPTIONS.PARTICIPANT_EDIT,
              actionCode: ACTION_CODE.WEB_PART_EDIT,
              action: "When saving a participant (editing)",
              detail: `Edited the participant ${data.firstName} ${data.lastName}`,
              transactionDate: new Date(),
              accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
              createdBy: userInfo?._id as string,
              targetId: data._id,
              name: `${userInfo?.firstName} ${userInfo?.lastName}`,
            });

            // AC04: Redirect to participants list keeping filters
            // The filters are maintained in the context, so we just navigate to the list
            // The onClose handler will call refetchParticipants() which will refresh with current filters
          },
          onError: (error: ErrorResponse) => {
            showSimpleAlert(
              (Array.isArray(error.message)
                ? error.message[0]
                : error.message) ||
                error.error ||
                "An error occurred",
            );
          },
        },
      );
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSimpleAlert(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred",
      );
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        isPending={false}
        title="Participant"
        theme={theme}
        size={{
          width: "100%",
          minHeight: 600,
          maxHeight: 900,
          maxWidth: 860,
        }}
        content={
          <Box sx={{ width: "100%", minHeight: 380, padding: "1rem 0" }}>
            <Grid container rowSpacing={1.6} columnSpacing={0.8}>
              <Grid size={12}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="titleMedium"
                    sx={{ color: theme.palette.primary.main, mb: 1 }}
                  >
                    General information
                  </Typography>
                  {/* Menu button in same line as title */}
                  <Box>
                    <IconButton
                      aria-label="more actions"
                      aria-controls={menuOpen ? "participant-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={menuOpen ? "true" : undefined}
                      onClick={handleMenuOpen}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      id="participant-menu"
                      anchorEl={anchorEl}
                      open={menuOpen}
                      onClose={handleMenuClose}
                      MenuListProps={{
                        "aria-labelledby": "more-button",
                      }}
                    >
                      {participant.status === ParticipantStatus.ACTIVE && (
                        <MenuItem
                          onClick={handleToggleMobileLogin}
                          sx={{ color: theme.palette.text.primary }}
                        >
                          <ListItemIcon
                            sx={{ color: theme.palette.text.primary }}
                          >
                            <PowerSettingsNewIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            sx={{
                              "& .MuiListItemText-primary": {
                                color: theme.palette.text.primary,
                              },
                            }}
                          >
                            {isMobileLoginActive
                              ? "Disable mobile login"
                              : "Enable mobile login"}
                          </ListItemText>
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={handleDelete}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <ListItemIcon sx={{ color: theme.palette.error.main }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: theme.palette.error.main,
                            },
                          }}
                        >
                          Delete
                        </ListItemText>
                      </MenuItem>
                    </Menu>
                  </Box>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="firstName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="firstName"
                      label={t("label.first-name")}
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.firstName}
                      helperText={
                        errors.firstName ? errors.firstName.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="lastName"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="lastName"
                      label={t("label.last-name")}
                      variant="outlined"
                      required
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.lastName}
                      helperText={
                        errors.lastName ? errors.lastName.message : null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="email"
                      label={t("label.email")}
                      required
                      disabled={true}
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.email}
                      helperText={errors.email ? errors.email.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="phoneNumber"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <PhoneNumberInput
                      {...field}
                      defaultCode={country}
                      value={field.value}
                      label={t("label.phone-number")}
                      variant="outlined"
                      className="flex flex-1 w-full"
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.phoneNumber}
                      helperText={
                        errors.phoneNumber ? errors.phoneNumber.message : null
                      }
                      required={phoneMandatory}
                    />
                  )}
                />
              </Grid>
              {dateOfBirthVisible ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    render={({ field }) => (
                      <div>
                        <Datepicker
                          {...field}
                          label={t("label.date-birth", {
                            format:
                              country === "US" ? "mm/dd/yyyy" : "dd/mm/yyyy",
                          })}
                          onChange={(value) => {
                            field.onChange(
                              value !== undefined ? value?.toDate() : undefined,
                            );
                          }}
                          value={
                            field.value
                              ? dayjs(
                                  field.value as string | number | Date | Dayjs,
                                )
                              : null
                          }
                          error={!!errors.dateOfBirth}
                          helperText={errors.dateOfBirth?.message || ""}
                          required={dateOfBirthMandatory}
                        />
                      </div>
                    )}
                  />
                </Grid>
              ) : null}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="cin"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="cin"
                      label={t("label.cin")}
                      required={cinMandatory}
                      variant="outlined"
                      value={field.value}
                      onChange={(value) => field.onChange(value)}
                      error={!!errors.cin}
                      helperText={errors.cin ? errors.cin.message : null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              {/* Primary Address Section */}
              <Grid size={12}>
                <Typography
                  variant="titleMedium"
                  sx={{ color: theme.palette.primary.main, mb: 1 }}
                >
                  Primary address
                </Typography>
              </Grid>
              <Grid size={12}>
                <Controller
                  name="primaryAddressLine1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="primaryAddressLine1"
                      label="Address line 1"
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).primaryAddressLine1}
                      helperText={
                        (errors as any).primaryAddressLine1?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="primaryAddressLine2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="primaryAddressLine2"
                      label="Address line 2"
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).primaryAddressLine2}
                      helperText={
                        (errors as any).primaryAddressLine2?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="primaryCity"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="primaryCity"
                      label={country === "US" ? "City" : "Town / City"}
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).primaryCity}
                      helperText={(errors as any).primaryCity?.message || null}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              {country === "US" ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="primaryState"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        id="primaryState"
                        label="State"
                        data={getUSStatesForSelect()}
                        value={
                          typeof field.value === "string" ? field.value : ""
                        }
                        handleChange={(value) => field.onChange(value)}
                        error={!!(errors as any).primaryState}
                        errorMessage={(errors as any).primaryState?.message}
                      />
                    )}
                  />
                </Grid>
              ) : country === "UK" ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="primaryCounty"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="primaryCounty"
                        label="County"
                        variant="outlined"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!(errors as any).primaryCounty}
                        helperText={
                          (errors as any).primaryCounty?.message || null
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              ) : null}
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="primaryZipCode"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="primaryZipCode"
                      label={country === "US" ? "ZIP" : "Postal code"}
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).primaryZipCode}
                      helperText={
                        (errors as any).primaryZipCode?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Secondary Address Section */}
              <Grid size={12}>
                <Typography
                  variant="titleMedium"
                  sx={{ color: theme.palette.primary.main, mb: 1, mt: 2 }}
                >
                  Secondary address
                </Typography>
              </Grid>
              <Grid size={12}>
                <Controller
                  name="secondaryAddressLine1"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="secondaryAddressLine1"
                      label="Address line 1"
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).secondaryAddressLine1}
                      helperText={
                        (errors as any).secondaryAddressLine1?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Controller
                  name="secondaryAddressLine2"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="secondaryAddressLine2"
                      label="Address line 2"
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).secondaryAddressLine2}
                      helperText={
                        (errors as any).secondaryAddressLine2?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                  name="secondaryCity"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="secondaryCity"
                      label={country === "US" ? "City" : "Town / City"}
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).secondaryCity}
                      helperText={
                        (errors as any).secondaryCity?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
              {country === "US" ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="secondaryState"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        id="secondaryState"
                        label="State"
                        data={getUSStatesForSelect()}
                        value={
                          typeof field.value === "string" ? field.value : ""
                        }
                        handleChange={(value) => field.onChange(value)}
                        error={!!(errors as any).secondaryState}
                        errorMessage={(errors as any).secondaryState?.message}
                      />
                    )}
                  />
                </Grid>
              ) : country === "UK" ? (
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Controller
                    name="secondaryCounty"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="secondaryCounty"
                        label="County"
                        variant="outlined"
                        value={field.value || ""}
                        onChange={(e) => field.onChange(e.target.value)}
                        error={!!(errors as any).secondaryCounty}
                        helperText={
                          (errors as any).secondaryCounty?.message || null
                        }
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              ) : null}
              <Grid size={{ xs: 12, sm: 6 }} sx={{ mb: 3 }}>
                <Controller
                  name="secondaryZipCode"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      id="secondaryZipCode"
                      label={country === "US" ? "ZIP" : "Postal code"}
                      variant="outlined"
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value)}
                      error={!!(errors as any).secondaryZipCode}
                      helperText={
                        (errors as any).secondaryZipCode?.message || null
                      }
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        }
        actions={
          <Box
            sx={{
              display: "flex",
              gap: "10px",
            }}
          >
            <Button
              variantType="cancel"
              onClick={onClose}
              disabled={isPending}
              label={t("button.cancel")}
              sx={{ textTransform: "uppercase" }}
            />
            <Button
              variantType="save"
              onClick={async () => {
                handleSubmit(
                  (data) => {
                    onSubmit(data);
                  },
                  () => {
                    // Validation errors are handled by the form
                  },
                )();
              }}
              disabled={isPending}
              isLoading={isPending}
              label="Save"
              sx={{ textTransform: "uppercase" }}
            />
          </Box>
        }
      />{" "}
      {showDeleteModal && (
        <AlertDialog
          open={showDeleteModal}
          title="Delete"
          description="Are you you want to delete this registry?"
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
      {showToggleMobileLoginModal && (
        <AlertDialog
          open={showToggleMobileLoginModal}
          title={
            isMobileLoginActive ? "Disable mobile login" : "Enable mobile login"
          }
          description={
            isMobileLoginActive
              ? "Are you sure you want to disable the mobile login to this participant?"
              : "Are you sure you want to enable the mobile login to this participant?"
          }
          onCancel={() => setShowToggleMobileLoginModal(false)}
          onConfirm={handleConfirmToggleMobileLogin}
          confirmText={isMobileLoginActive ? "Disable" : "Enable"}
          cancelText="Cancel"
        />
      )}
      <PermissionDeniedModal
        open={showPermissionDeniedModal}
        onClose={() => setShowPermissionDeniedModal(false)}
      />
    </>
  );
};

export default ParticipantUpdateModal;
